import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SubsWall, { STYLE_PALETTES, type SubTile, type TileStyle } from "../components/SubsWall";
import "./LandingV3.css";

// ----------------------------------------------------------------------------
// Hero wall — uses Graffiti tile styles on the Glow theme background so the
// tiles glow against the dark page seamlessly.
// ----------------------------------------------------------------------------

function makeHeroTiles(): SubTile[] {
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle }> = [
    { name: "KAIROS",   message: "first one in. shipping forever.",                  monthlyCents: 500,   style: "spray" },
    { name: "ZINC",     message: "$5/mo for a forever spot. fair.",                  monthlyCents: 500,   style: "chalk" },
    { name: "lo-fi-ghost", message: "playing your VOD on loop while I code",         monthlyCents: 1000,  style: "neon", blink: true },
    { name: "FRACTAL",  message: "the soundtrack saved my run",                      monthlyCents: 2500,  style: "neon", blink: true, anniversaryMonths: 7 },
    { name: "VEXLINE",  message: "watching since the API days",                      monthlyCents: 5000,  style: "carved", badge: "shield" },
    { name: "BIGSHOT",  message: "yes I bought the biggest tile.",                   monthlyCents: 10000, style: "neon", blink: true, badge: "crown" },
    { name: "AURORA",   message: "day-one founder. forever.",                        monthlyCents: 25000, style: "neon", blink: true, badge: "star", founderNumber: 1, anniversaryMonths: 12 },
  ];
  return seed.map((s, i) => ({
    id: `h${i}`,
    name: s.name,
    message: s.message,
    style: s.style,
    color: STYLE_PALETTES[s.style][(i + 1) % 8],
    xPct: 14 + ((i * 73) % 74),
    yPct: 22 + ((i * 41) % 64),
    monthlyCents: s.monthlyCents,
    anniversaryMonths: s.anniversaryMonths ?? 0,
    founderNumber: s.founderNumber ?? null,
    boostUntil: null,
    fadeStartedAt: null,
    badge: s.badge ?? null,
    blink: s.blink ?? false,
  }));
}

// ----------------------------------------------------------------------------
// Animated MRR count-up — gives the landing some life on first paint.
// ----------------------------------------------------------------------------

function useCountUp(target: number, durationMs = 1400): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (t: number): void => {
      const p = Math.min(1, (t - start) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return n;
}

// ----------------------------------------------------------------------------

export default function LandingV3(): JSX.Element {
  const [tiles] = useState<SubTile[]>(makeHeroTiles);
  const supporters = useCountUp(2147);
  const paidOut = useCountUp(184320);

  return (
    <div className="gl-page lv3">
      {/* NAV */}
      <nav className="lv3-nav gl-container">
        <Link to="/" className="lv3-brand">
          <span className="lv3-brand-glyph" aria-hidden="true"></span>
          SubsWall
        </Link>
        <div className="lv3-nav-right">
          <a href="#how">How</a>
          <a href="#why">Why</a>
          <a href="#pricing">Pricing</a>
          <Link to="/login" className="lv3-nav-signin">Sign in</Link>
          <Link to="/login" className="gl-btn gl-btn-primary gl-btn-sm">Claim handle →</Link>
        </div>
      </nav>

      {/* HERO */}
      <header className="lv3-hero gl-container">
        <div className="gl-eyebrow gl-fade gl-fade-d1">For creators · YouTube · Twitch · Podcast · Substack</div>
        <h1 className="gl-display lv3-h1 gl-fade gl-fade-d2">
          The wall<br/>
          <span className="lv3-h1-em">that pays you</span><br/>
          forever.
        </h1>
        <p className="gl-lead lv3-lead gl-fade gl-fade-d3">
          Recurring subscriptions, made public. Your supporters sign your wall once
          and stay named on your channel for as long as they support you. Patreon
          makes you a content factory. We make your fans famous.
        </p>
        <div className="lv3-hero-ctas gl-fade gl-fade-d4">
          <Link to="/login" className="gl-btn gl-btn-primary gl-btn-lg">
            Claim your wall →
          </Link>
          <Link to="/demo" className="gl-btn gl-btn-ghost gl-btn-lg">
            See a live wall
          </Link>
        </div>

        {/* Live counters strip */}
        <div className="lv3-stats gl-fade gl-fade-d4">
          <div>
            <div className="gl-mono lv3-stat-num">{supporters.toLocaleString()}</div>
            <div className="lv3-stat-lbl">supporters on walls</div>
          </div>
          <div>
            <div className="gl-mono lv3-stat-num">${paidOut.toLocaleString()}</div>
            <div className="lv3-stat-lbl">paid to creators</div>
          </div>
          <div>
            <div className="gl-mono lv3-stat-num">4.9<small>%</small></div>
            <div className="lv3-stat-lbl">flat fee · zero upsells</div>
          </div>
        </div>

        {/* The wall itself, big and embedded */}
        <div className="lv3-hero-wall gl-fade gl-fade-d4">
          <SubsWall tiles={tiles} theme="glow" aspectRatio={20 / 9} />
          <div className="lv3-hero-wall-caption">
            <span className="lv3-pulse"></span> A real SubsWall · drag your own tile when you sign on
          </div>
        </div>
      </header>

      {/* HOW — bento grid */}
      <section className="lv3-bento gl-container" id="how">
        <div className="gl-eyebrow">How it works</div>
        <h2 className="gl-display lv3-h2">Three steps. No newsletter.</h2>

        <div className="lv3-bento-grid">
          <div className="lv3-bento-card lv3-bento-1">
            <div className="lv3-step">01</div>
            <h3>Claim your handle.</h3>
            <p>Sign in. Pick a name. Link Stripe. <code>subswall.com/yourname</code> goes live in 30 seconds.</p>
            <div className="lv3-bento-mock">
              <span className="lv3-mono-input">subswall.com/<em>your-handle</em></span>
            </div>
          </div>

          <div className="lv3-bento-card lv3-bento-2">
            <div className="lv3-step">02</div>
            <h3>Fans sign in 30 seconds.</h3>
            <p>One screen. Name + line + tile style + Stripe checkout. They're on, named, recurring.</p>
            <div className="lv3-bento-mock lv3-bento-mock-tiles">
              <span className="lv3-mock-tile lv3-mock-t1">SARAH K.</span>
              <span className="lv3-mock-tile lv3-mock-t2">MARCUS</span>
              <span className="lv3-mock-tile lv3-mock-t3">AURORA</span>
            </div>
          </div>

          <div className="lv3-bento-card lv3-bento-3">
            <div className="lv3-step">03</div>
            <h3>The wall does the rest.</h3>
            <p>Cancellation fades over 7 days. Top tiers look unmistakably bigger. Anniversaries earn gold dots.</p>
          </div>

          <div className="lv3-bento-card lv3-bento-wide">
            <div className="lv3-step">⌘</div>
            <h3>Embed anywhere.</h3>
            <p>YouTube About page. Video descriptions. Your own site. One iframe, updates live, signature handshake with your brand.</p>
            <pre className="lv3-code">{`<iframe src="https://subswall.com/you/embed"
        width="100%" height="600"
        loading="lazy"/>`}</pre>
          </div>

          <div className="lv3-bento-card lv3-bento-final">
            <div className="lv3-step lv3-step-accent">$</div>
            <h3>4.9% flat.</h3>
            <p>No tiers for creators. No per-supporter fee. No "Pro plan." Stripe processes; we take 4.9%; you keep 95.1%.</p>
          </div>
        </div>
      </section>

      {/* WHY — comparison */}
      <section className="lv3-vs gl-container" id="why">
        <div className="gl-eyebrow">Why not Patreon</div>
        <h2 className="gl-display lv3-h2">
          Patreon makes you a content factory.<br />
          <span className="lv3-h1-em">We make your fans famous.</span>
        </h2>

        <div className="lv3-vs-grid">
          <div className="lv3-vs-col">
            <div className="lv3-vs-col-head lv3-vs-them">Patreon</div>
            <ul>
              <li className="lv3-x">Tiers, posts, DMs, comments — admin work forever</li>
              <li className="lv3-x">Hidden behind a paywall · fans invisible</li>
              <li className="lv3-x">8–12% take · Pro plan upsells</li>
              <li className="lv3-x">If you stop posting, you stop earning</li>
            </ul>
          </div>
          <div className="lv3-vs-col lv3-vs-us">
            <div className="lv3-vs-col-head lv3-vs-us-head">SubsWall</div>
            <ul>
              <li className="lv3-tick">Zero admin · the wall is the product</li>
              <li className="lv3-tick">Public wall · cancellation visibly fades</li>
              <li className="lv3-tick">4.9% flat · forever · no upsells</li>
              <li className="lv3-tick">Subscribers stay even when you don't post</li>
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="lv3-pricing gl-container" id="pricing">
        <div className="lv3-pricing-text">
          <div className="gl-eyebrow">Pricing</div>
          <h2 className="gl-display lv3-h2">One number.<br /><span className="lv3-h1-em">4.9%</span> of what you earn.</h2>
          <p className="gl-lead">
            Plus Stripe's standard processing. No monthly minimum. No per-supporter fee.
            No "Pro plan." Just one line.
          </p>
          <Link to="/login" className="gl-btn gl-btn-primary gl-btn-lg">Start in 60 seconds →</Link>
        </div>

        <div className="lv3-pricing-card gl-card-accent">
          <div className="gl-eyebrow">Supporter pricing</div>
          <h3 className="lv3-ladder-title">Tiers grow into something real.</h3>
          <div className="lv3-ladder">
            <div><span>☕</span><strong className="gl-mono">$5</strong><em>a coffee</em></div>
            <div><span>🍕</span><strong className="gl-mono">$10</strong><em>a pizza</em></div>
            <div><span>🍣</span><strong className="gl-mono">$25</strong><em>sushi</em></div>
            <div><span>🍷</span><strong className="gl-mono">$50</strong><em>a bottle of wine</em></div>
            <div><span>🍽️</span><strong className="gl-mono">$100</strong><em>a tasting menu</em></div>
            <div><span>✈️</span><strong className="gl-mono">$250</strong><em>a weekend trip</em></div>
          </div>
          <div className="lv3-ladder-foot">Higher tiers unlock bigger tiles, gold foil, and a permanent slot.</div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="lv3-final gl-container">
        <h2 className="gl-display lv3-final-h">
          Your channel deserves a <span className="lv3-h1-em">monument</span>,<br />
          not a feed.
        </h2>
        <p className="gl-lead">60 seconds. Claim your handle.</p>
        <div className="lv3-final-ctas">
          <Link to="/login" className="gl-btn gl-btn-primary gl-btn-lg">Claim your wall →</Link>
          <Link to="/demo" className="gl-btn gl-btn-ghost gl-btn-lg">See a live wall</Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lv3-footer">
        <div className="gl-container lv3-footer-inner">
          <div className="lv3-brand">
            <span className="lv3-brand-glyph"></span> SubsWall
          </div>
          <div className="lv3-footer-meta">
            <span>4.9% take · 95.1% to creators</span>
            <span>·</span>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
